<template>
    <div
        :class="componentClasses"
    >
        <div class="chat-assistent-interaction-item__content-container">
            <div class="chat-assistent-interaction-item__content">
                <div
                    v-html="parsedContent"
                />
            </div>
            <div class="chat-assistent-interaction-item__avatar">
                <assistent-avatar :assistent="props.assistent" />
            </div>
        </div>
        <div class="chat-assistent-interaction-item__footer">
            <text-decorator :size="FontSize.xs">
                {{ formatTime(interaction.createdAt) }}
            </text-decorator>
        </div>
    </div>
</template>

<script setup lang="ts">
    import type { Assistent, Interaction } from '@keyvue/one-api-client';
    import { formatTime } from '@/shared/utils/date';
    import { FontSize } from '@keyvue/one-ui';
    import AssistentAvatar from '@/ai/assistent/components/AssistentAvatar.vue';
    import useAppBranding from '@/services/app-branding/composables/useAppBranding';
    import { marked, type Tokens } from 'marked';
    import { computed } from 'vue';

    const props = defineProps<{
        interaction: Interaction,
        assistent: Assistent,
    }>();

    const { hasHeylohaBranding } = useAppBranding();

    const componentClasses = computed(() => ({
        'chat-assistent-interaction-item': true,
        'chat-assistent-interaction-item--heyloha': hasHeylohaBranding(),
    }));

    const parsedContent = computed(() => {
        const localRenderer = new marked.Renderer();

        function renderLink({ href, text }: Tokens.Link) {
            return `<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`;
        }

        function renderImage({ href, title, text }: Tokens.Image) {
            return `
            <img
                src="${href}"
                alt="${text}"
                title="${title || ''}"
                style="width: 100%; height: auto; object-fit: contain;"
            />
        `;
        }

        localRenderer.link = renderLink;
        localRenderer.image = renderImage;

        return marked(props.interaction.content ?? '', { renderer: localRenderer });
    });
</script>

<style scoped lang="scss">
    .chat-assistent-interaction-item {
        max-width: 80%;
        display: flex;
        align-self: flex-end;
        align-items: flex-end;
        justify-content: flex-end;
        flex-wrap: wrap;
        flex-direction: column;

        &__avatar {
            width: size(lg);
            height: size(lg);
            padding: 0 size(sm);
        }

        &__content-container {
            display: flex;
            align-items: flex-end;
            width: max-content;
            max-width: 100%;
        }

        &__content {
            width: calc(100% - 50px);
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
            flex-wrap: wrap;
            gap: size(sm);

            & > div {
                padding: size(md);
                border-radius: size(sm);
                box-sizing: border-box;
                background-color: #DDE8F7;
                color: var(--theme-colors-default--gray-700);
                border-bottom: 1px solid var(--theme-colors-default--gray-300);
                box-shadow: 0 0 5px 0 rgba(0,0,0,0.15);
                font-size: font-size(sm);
                line-height: 19px;

                :deep(p + p) {
                    margin-top: size(md);
                }

                :deep(img) {
                    max-width: 100%;
                    height: auto;
                    margin: .5rem 0;
                }
            }
        }

        &__footer {
            padding-top: 5px;
            padding-right: 50px;
        }
    }

    .chat-assistent-interaction-item--heyloha {
        .chat-assistent-interaction-item__content {
            & > div {
                background-color: #FAD5CD;
            }
        }
    }
</style>
